import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import './InstallPrompt.css';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detectar se é mobile
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobile = /android|iphone|ipad|ipod/.test(userAgent);
      setIsMobile(mobile);
      return mobile;
    };

    const mobile = checkMobile();

    // Se não é mobile, não mostrar nada
    if (!mobile) {
      return;
    }

    // Detectar se o app já está instalado
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Detectar plataforma
    const userAgent = navigator.userAgent.toLowerCase();
    let detectedPlatform = null;

    if (/android/.test(userAgent)) {
      detectedPlatform = 'android';
    } else if (/iphone|ipad|ipod/.test(userAgent)) {
      detectedPlatform = 'ios';
    }

    setPlatform(detectedPlatform);

    // Listener para o evento beforeinstallprompt (Android)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Mostrar prompt apenas em Android
      if (detectedPlatform === 'android') {
        setShowPrompt(true);
      }
    };

    // Listener para quando o app é instalado
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Para iOS, mostrar instruções depois de um tempo
    if (detectedPlatform === 'ios') {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleClose = () => {
    setShowPrompt(false);
  };

  // Não mostrar se não é mobile, já está instalado ou o prompt não deve ser exibido
  if (!isMobile || isInstalled || !showPrompt) {
    return null;
  }

  // Renderizar diferentes mensagens conforme a plataforma
  if (platform === 'ios') {
    return (
      <div className="install-prompt ios-prompt">
        <div className="install-prompt-content">
          <div className="install-prompt-header">
            <h3>Instale o App</h3>
            <button onClick={handleClose} className="install-prompt-close">
              <X size={20} />
            </button>
          </div>
          <p className="install-prompt-text">
            Toque em <strong>Compartilhar</strong> e depois em <strong>Adicionar à Tela de Início</strong> para instalar o app APEM.
          </p>
          <div className="install-prompt-steps">
            <div className="step">
              <span className="step-number">1</span>
              <span>Toque em Compartilhar</span>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <span>Selecione "Adicionar à Tela de Início"</span>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <span>Confirme o nome do app</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android
  return (
    <div className="install-prompt chrome-prompt">
      <div className="install-prompt-content">
        <div className="install-prompt-header">
          <h3>Instale o App APEM</h3>
          <button onClick={handleClose} className="install-prompt-close">
            <X size={20} />
          </button>
        </div>
        <p className="install-prompt-text">
          Instale o app APEM no seu dispositivo para acessar offline e ter uma experiência melhor.
        </p>
        <div className="install-prompt-actions">
          <button onClick={handleInstallClick} className="install-prompt-button install-button">
            <Download size={18} />
            Instalar Agora
          </button>
          <button onClick={handleClose} className="install-prompt-button cancel-button">
            Agora Não
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
